import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { z } from 'zod';

const signInSchema = z.object({
  email: z.string()
    .trim()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .toLowerCase(),
  password: z.string()
    .min(1, 'Password is required')
    .max(128, 'Password must be less than 128 characters')
});

interface SignInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestAccess: () => void;
}

export const SignInModal = ({ open, onOpenChange, onRequestAccess }: SignInModalProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
      setEmail('');
      setPassword('');
      setError(null);
      setMagicLinkSent(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    const result = signInSchema.safeParse({ email, password });
    if (!result.success) {
      setError(result.error.errors[0].message);
      setLoading(false);
      return;
    }

    const { email: validEmail, password: validPassword } = result.data;
    
    const { data, error } = await supabase.auth.signInWithPassword({ 
      email: validEmail, 
      password: validPassword 
    });
    
    setLoading(false);
    
    if (error) {
      setError(error.message);
    } else {
      if (data.user) {
        try {
          const userAgent = navigator.userAgent;
          const getBrowser = () => {
            if (userAgent.includes('Firefox')) return 'Firefox';
            if (userAgent.includes('Chrome')) return 'Chrome';
            if (userAgent.includes('Safari')) return 'Safari';
            if (userAgent.includes('Edge')) return 'Edge';
            return 'Unknown';
          };
          
          const getDeviceType = () => {
            if (/mobile/i.test(userAgent)) return 'Mobile';
            if (/tablet/i.test(userAgent)) return 'Tablet';
            return 'Desktop';
          };

          await supabase.from('login_history').insert({
            user_id: data.user.id,
            user_agent: userAgent,
            device_type: getDeviceType(),
            browser: getBrowser(),
            login_method: 'password'
          });
        } catch (logError) {
          console.error('Error logging login:', logError);
        }
      }
      
      const userName = data.user?.user_metadata?.name || 
                       data.user?.email?.split('@')[0] || 
                       'there';
      const displayName = userName.charAt(0).toUpperCase() + userName.slice(1);
      
      toast.success(
        `Welcome back, ${displayName}! ✨ Ready to discover extraordinary stays?`,
        {
          duration: 4000,
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#ffffff',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
          },
        }
      );
      onOpenChange(false);
    }
  };

  const onMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setError(null);
    setMagicLinkSent(false);
    setLoading(true);
    
    const { error } = await supabase.auth.signInWithOtp({
      email: email.toLowerCase().trim(),
      options: {
        emailRedirectTo: window.location.href,
      },
    });
    
    setLoading(false);
    
    if (error) {
      setError(error.message);
      setMagicLinkSent(false);
    } else {
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check your email.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-charcoal border border-gold/20 text-ivory w-full max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] md:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-sans text-ivory flex items-center gap-2">
            Sign in
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
            </motion.div>
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base text-ivory/70">
            Welcome back to Exora Club
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="mt-4 sm:mt-6 space-y-3 sm:space-y-4" name="signin-form">
          <Input
            type="email"
            name="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`bg-muted/50 border-0 text-ivory h-12 focus-visible:ring-2 focus-visible:ring-gold ${error ? 'ring-2 ring-red-500' : ''}`}
            aria-label="Email address"
            required
            autoComplete="email"
          />
          
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`bg-muted/50 border-0 text-ivory h-12 pr-14 focus-visible:ring-2 focus-visible:ring-gold ${error ? 'ring-2 ring-red-500' : ''}`}
              aria-label="Password"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ivory/60 hover:text-gold transition-colors p-2 rounded-lg hover:bg-gold/10 min-h-[40px] min-w-[40px] flex items-center justify-center"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                window.location.href = '/forgot-password';
              }}
              className="text-xs text-muted-foreground hover:text-gold transition-colors"
            >
              Forgot password?
            </button>
          </div>
          
          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              {error}
            </p>
          )}
          
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gold hover:bg-gold-glow text-charcoal font-semibold h-12 shadow-lg shadow-gold/20"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gold/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-charcoal px-2 text-muted-foreground">Or</span>
            </div>
          </div>
          
          <Button
            type="button"
            onClick={onMagicLink}
            disabled={loading || magicLinkSent}
            variant="outline"
            className="w-full mt-4 border-gold/20 hover:bg-gold/5 text-ivory hover:text-ivory h-12"
          >
            {magicLinkSent ? "Magic link sent ✓" : "Send magic link"}
          </Button>
          
          {magicLinkSent && (
            <p className="text-xs text-ivory/60 mt-3 text-center">
              Check your email for the sign-in link
            </p>
          )}
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have access?{" "}
            <button
              onClick={() => {
                onOpenChange(false);
                onRequestAccess();
              }}
              className="text-gold hover:text-gold-glow font-medium transition-colors"
            >
              Request access
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};